// V97.4 Phase V3.A — Catalogue Ortho-Analytic (tests + mapping markers).
// Date : 2026-05-12
//
// ⚠⚠ CE FICHIER N'EST PAS LA SOURCE DES PRIX NI DE L'OFFRE ⚠⚠
//
// Mise au point du 2026-08-22. Malgré ce que dit l'en-tête d'origine
// ci-dessous, plus rien ici ne sert au catalogue commercial :
//
//   L'OFFRE ET LES PRIX vivent dans la table `lab_tests` de Supabase, que
//   `AnalysisSuggestionModal` lit directement. Ils y sont justes, vérifiés le
//   22.08 contre la Preisliste V12.0/04-2026.
//
//   SEUL `expected_markers` est encore lu, via getExpectedMarkersForTest,
//   pour dire quels marqueurs attendre d'une analyse prescrite.
//
// Les `price_chf` ci-dessous sont donc PÉRIMÉS et inertes. Ne pas s'en
// servir, ne pas s'y fier. Ils m'ont fait signaler deux fois de fausses
// erreurs le 22.08 : un profil thyroïdien à 95 CHF qui n'existe nulle part,
// et deux profils « à composer » qui n'ont jamais été prescriptibles.
//
// De même, cinq entrées ici ne correspondent à AUCUNE ligne de `lab_tests` :
// thyroide_complete, hormones_feminines, inflammation, metabolique et
// genetique_nutrition. Elles sont inertes — getTest n'est appelé qu'avec des
// codes venus de la base — mais elles trompent la lecture.
//
// ⚠ Et genetique_nutrition porte `lab: 'Ortho-Analytic'` alors qu'Ortho ne
// fait pas d'ADN : c'est MGD (ELIPSEgenes).
//
// ─────────────── en-tête d'origine, conservé pour l'historique ──────────
//
// Source de vérité pour :
//   - tests disponibles chez le partenaire Ortho-Analytic
//   - prix Anissa (coût payé par Anissa au labo)
//   - prix cliente (s'il y a marge praticienne)
//   - laboratoire émetteur (Ortho-Analytic, MGD, autres si besoin)
//   - catégorie clinique
//   - marqueurs attendus (mapping vers ./markers.js)
//   - axes cliniques principaux ciblés
//
// ⚠️ V3.A : squelette structuré. Plusieurs prix sont des PLACEHOLDERS à
// confirmer avec Marie-Aude au RDV2 (HMA capillaire, Microbiome NGS, ADN,
// programme partenaire). Les entrées marquées `price_chf: null` ou avec
// commentaire TODO doivent être complétées avant usage commercial.
//
// Cohérence importante :
//   - shape compatible avec _clinicalContext.fr.js (champs name, lab,
//     category, price_chf reconnus par formatTest())
//   - shape compatible avec buildClinicalContext (champ category, mapping
//     vers axes cliniques BC.5D.2 : hormonal / microbiote / inflammation /
//     carence / metabolique / autre)
//
// Source des prix actuels :
//   Ortho-Analytic Preisliste Integrative Medizin V12.04.2026
//   (mémoire 2026-05-07 — décryptage tarif)

import { MARKERS_CATALOG } from './markers.js';

/**
 * @typedef {Object} OrthoTest
 * @property {string}   code                  - id stable (snake_case)
 * @property {string}   name                  - libellé affiché ("Mikroernährung")
 * @property {string}   lab                   - laboratoire ("Ortho-Analytic" | "MGD")
 * @property {string}   category              - catégorie technique ("sang" | "microbiome" | "genetique" | "metaux_lourds" | "hormones" | "vitamines_mineraux")
 * @property {string}   clinical_category     - catégorie clinique (axe principal — pour mapping BC.5D.2)
 * @property {number|null} price_chf          - prix labo payé par Anissa (null = à compléter)
 * @property {number}   [price_cliente_chf]   - prix facturé cliente (si différent de price_chf)
 * @property {string[]} expected_markers      - codes markers (cf. ./markers.js)
 * @property {string}   [description]         - 1 phrase contexte clinique
 * @property {boolean}  [is_active]           - true par défaut, false = retiré du catalogue
 * @property {string}   [todo]                - note interne si entrée incomplète
 */

/**
 * Catalogue Ortho-Analytic + MGD. Clé = code stable.
 * @type {Record<string, OrthoTest>}
 */
export const ORTHO_TESTS_CATALOG = {

  // ═══════════════════════════════════════════════════════════════
  // PROFILS SANG — Ortho-Analytic
  // ═══════════════════════════════════════════════════════════════

  ortho_mikroernaehrung: {
    code: 'ortho_mikroernaehrung',
    name: 'Mikroernährung (micronutrition)',
    lab: 'Ortho-Analytic',
    category: 'sang',
    clinical_category: 'carence',
    price_chf: 248,
    expected_markers: [
      'vit_d_25oh', 'vit_b12', 'folates',
      'fer_serique', 'ferritine', 'transferrine_saturation',
      'magnesium_globulaire', 'zinc', 'selenium',
      'omega_3_index',
    ],
    description: 'Bilan micronutrition complet : vitamines, minéraux, oligo-éléments, oméga-3.',
    is_active: true,
  },

  ortho_thyroide_complete: {
    code: 'ortho_thyroide_complete',
    name: 'Profil thyroïdien complet',
    lab: 'Ortho-Analytic',
    category: 'sang',
    clinical_category: 'hormonal',
    price_chf: 95,
    expected_markers: ['tsh', 't3_libre', 't4_libre', 'anti_tpo'],
    description: 'Bilan thyroïdien étendu : TSH + T3/T4 libres + anti-TPO.',
    is_active: true,
  },

  ortho_inflammation: {
    code: 'ortho_inflammation',
    name: 'Profil inflammation systémique',
    lab: 'Ortho-Analytic',
    category: 'sang',
    clinical_category: 'inflammation',
    price_chf: null, // TODO RDV2 Marie-Aude
    expected_markers: ['crp_us', 'fibrinogene', 'vitesse_sedimentation', 'homocysteine'],
    description: 'Marqueurs inflammatoires de bas grade.',
    is_active: true,
    todo: 'Confirmer prix exact + composition exacte du profil',
  },

  ortho_hormones_feminines: {
    code: 'ortho_hormones_feminines',
    name: 'Profil hormonal féminin',
    lab: 'Ortho-Analytic',
    category: 'sang',
    clinical_category: 'hormonal',
    price_chf: null, // TODO RDV2
    expected_markers: ['estradiol', 'progesterone', 'dhea_s', 'testosterone'],
    description: 'Hormones sexuelles + précurseurs surrénaliens.',
    is_active: true,
    todo: 'Confirmer prix et timing prélèvement (phase cycle)',
  },

  ortho_cortisol_day: {
    code: 'ortho_cortisol_day',
    name: 'Cortisol profil journée (4 prélèvements)',
    lab: 'Ortho-Analytic',
    category: 'sang',
    clinical_category: 'hormonal',
    price_chf: null, // TODO RDV2
    expected_markers: ['cortisol_profil_journee', 'cortisol_matin', 'dhea_s'],
    description: 'Évaluation du rythme HHS sur la journée (08h, 11h, 16h, 22h).',
    is_active: true,
    todo: 'Confirmer prix et logistique prélèvements à domicile',
  },

  ortho_metabolique: {
    code: 'ortho_metabolique',
    name: 'Profil métabolique',
    lab: 'Ortho-Analytic',
    category: 'sang',
    clinical_category: 'metabolique',
    price_chf: null, // TODO RDV2
    expected_markers: [
      'glycemie_jeun', 'hba1c', 'insuline',
      'cholesterol_total', 'ldl_cholesterol', 'hdl_cholesterol',
      'triglycerides',
    ],
    description: 'Bilan lipidique + glycémique + insulinorésistance fonctionnelle.',
    is_active: true,
    todo: 'Confirmer composition exacte et prix',
  },

  // ═══════════════════════════════════════════════════════════════
  // MICROBIOME — Ortho-Analytic
  // ═══════════════════════════════════════════════════════════════

  ortho_mikrobiom_complete_plus: {
    code: 'ortho_mikrobiom_complete_plus',
    name: 'Microbiome Complete Plus (NGS)',
    lab: 'Ortho-Analytic',
    category: 'microbiome',
    clinical_category: 'microbiote',
    price_chf: null, // TODO RDV2 Marie-Aude (mémoire indique manquant)
    expected_markers: [
      'diversite_microbiote',
      'akkermansia', 'faecalibacterium',
      'iga_secretoire', 'zonuline',
      'calprotectine', 'candida_albicans',
    ],
    description: 'Analyse NGS du microbiote intestinal : diversité, dysbiose, perméabilité.',
    is_active: true,
    todo: 'Confirmer prix exact + délai labo + format rapport',
  },

  // ═══════════════════════════════════════════════════════════════
  // MÉTAUX LOURDS — HMA capillaire
  // ═══════════════════════════════════════════════════════════════

  ortho_haarmineral_39: {
    code: 'ortho_haarmineral_39',
    name: 'HMA capillaire (métaux lourds)',
    lab: 'Ortho-Analytic',
    category: 'metaux_lourds',
    clinical_category: 'autre',
    price_chf: null, // TODO RDV2 (mémoire indique manquant)
    expected_markers: [
      // Codes à créer dans markers.js V3.B : mercure, plomb, aluminium, cadmium, arsenic
    ],
    description: 'Analyse minérale et toxique sur cheveu (reflet 6-12 dernières semaines).',
    is_active: true,
    todo: 'Confirmer prix + créer codes markers métaux lourds dans markers.js',
  },

  // ═══════════════════════════════════════════════════════════════
  // GÉNÉTIQUE — ADN nutrition / épigénétique
  // ═══════════════════════════════════════════════════════════════

  ortho_genetique_nutrition: {
    code: 'ortho_genetique_nutrition',
    name: 'Profil ADN nutrition fonctionnelle',
    lab: 'Ortho-Analytic',
    category: 'genetique',
    clinical_category: 'autre',
    price_chf: null, // TODO RDV2 (mémoire indique manquant)
    expected_markers: [
      // Codes à créer dans markers.js V3.B : MTHFR, COMT, APOE, FTO, GSTT1, GSTM1, etc.
    ],
    description: 'Polymorphismes liés à méthylation, détoxification, lipides, glucides.',
    is_active: true,
    todo: 'Confirmer prix + scope exact des SNPs analysés + créer codes markers ADN',
  },

  // ═══════════════════════════════════════════════════════════════
  // MGD (LaboMGD Genève) — laboratoire partenaire, à égalité avec Ortho
  // ═══════════════════════════════════════════════════════════════
  // V97.140 : MGD n'est plus un héritage en voie de migration. Anissa
  // travaille avec les DEUX labos, et une même cliente peut avoir des
  // analyses des deux. Le choix du labo lui appartient, cliente par cliente.
  //
  // ATTENTION — ces entrées ne sont PAS la source de vérité du catalogue.
  // Le catalogue proposé à Anissa vit dans la table Supabase `lab_tests` ;
  // ce fichier ne sert plus qu'à l'interprétation clinique (expected_markers
  // et clinical_category), y compris pour des tests retirés du catalogue.
  // C'est pourquoi getTest() renvoie aussi les entrées is_active:false : un
  // plan passé doit rester interprétable après retrait du test.
  //
  // Les 6 bilans MGD retenus par Anissa (validation du 2026-07-15) sont en
  // `pending_anissa_validation` dans Supabase et restent is_active:false ici
  // tant qu'elle n'a pas fourni les marqueurs qu'elle saisit. Réaligner les
  // deux sources en même temps : une entrée active ici avec une liste de
  // marqueurs incomplète est pire que pas d'entrée du tout, car le SaaS
  // affirme alors une liste fausse au lieu d'ouvrir la saisie libre
  // (cf. TSH manquante ci-dessous).

  mgd_bilan_nutritionnel_base: {
    code: 'mgd_bilan_nutritionnel_base',
    name: 'Bilan nutritionnel de base (MGD)',
    lab: 'MGD',
    category: 'sang',
    clinical_category: 'carence',
    price_chf: 233.6,
    // VIDE À DESSEIN — ne pas re-remplir depuis la grille commerciale.
    // La liste précédente ['vit_d_25oh','vit_b12','folates','ferritine',
    // 'magnesium_globulaire'] était FAUSSE : les vraies feuilles de résultat
    // MGD (2026-07-15) montrent que ce bilan rend aussi la TSH, un hémogramme
    // complet, l'HbA1c en 2 unités, l'insuline et le HOMA. Anissa aurait vu
    // 5 cases, reçu 13 résultats, et perdu le reste sans aucun message.
    //
    // Une liste vide bascule ResultCard en saisie libre (cf. ClientJourneyPage
    // « mode résultat libre uniquement ») : Anissa saisit ce qu'elle veut, rien
    // ne se perd. Une liste INCOMPLÈTE ment ; une liste vide dit « je ne sais
    // pas ». Tant qu'on n'a pas SA liste, ne rien affirmer est le seul choix
    // honnête.
    expected_markers: [],
    description: 'Bilan micronutrition LaboMGD. Équivalent MGD du Mikroernährung Ortho.',
    is_active: true,
  },

  mgd_microbiote_adn: {
    code: 'mgd_microbiote_adn',
    name: 'Microbiote ADN (MGD)',
    lab: 'MGD',
    category: 'microbiome',
    clinical_category: 'microbiote',
    price_chf: 173.6,
    // VIDE À DESSEIN — même raison que le bilan nutritionnel ci-dessus.
    // La liste précédente ['diversite_microbiote','akkermansia',
    // 'faecalibacterium'] tenait en 3 marqueurs pour un profil ADN de
    // microbiote qui en rend beaucoup plus. Saisie libre tant qu'Anissa n'a
    // pas fourni la sienne, plutôt qu'une liste qui en oublie la majorité.
    expected_markers: [],
    description: 'Profil ADN du microbiote intestinal (LaboMGD).',
    is_active: true,
  },

  mgd_elipsegenes: {
    code: 'mgd_elipsegenes',
    name: 'ELIPSEgenes (MGD)',
    lab: 'MGD',
    category: 'genetique',
    clinical_category: 'autre',
    price_chf: 358,
    // Déjà actif dans lab_tests depuis longtemps — ce is_active:false était
    // un reliquat du « switch vers Ortho » abandonné, et contredisait la prod.
    expected_markers: [],
    description: 'ADN nutritionnel ELIPSEgenes (LaboMGD). Consentement éclairé requis.',
    is_active: true,
  },
};

/**
 * Récupère un test par code. Retourne null si inconnu.
 */
export function getTest(code) {
  if (!code || typeof code !== 'string') return null;
  return ORTHO_TESTS_CATALOG[code] || null;
}

/**
 * Liste les tests actifs (filtre is_active === false).
 * Par défaut : seuls les tests utilisables actuellement.
 */
export function getActiveTests({ includeInactive = false } = {}) {
  const all = Object.values(ORTHO_TESTS_CATALOG);
  if (includeInactive) return all;
  return all.filter((t) => t.is_active !== false);
}

/**
 * Liste les tests pour un laboratoire donné ('Ortho-Analytic' | 'MGD').
 */
export function getTestsByLab(lab) {
  if (!lab) return [];
  return Object.values(ORTHO_TESTS_CATALOG).filter((t) => t.lab === lab && t.is_active !== false);
}

/**
 * Pour un code de test, retourne les marqueurs attendus enrichis depuis
 * le catalogue markers.js. Filtre les codes inconnus silencieusement.
 *
 * @param {string} testCode
 * @returns {Array<import('./markers').Marker>}
 */
export function getExpectedMarkersForTest(testCode) {
  const test = getTest(testCode);
  if (!test || !Array.isArray(test.expected_markers)) {
    // 2026-08-22 — ce silence a coûté trois mois. Les codes de ce fichier
    // avaient dérivé de ceux de la table `lab_tests` (`ortho_cortisol_journee`
    // contre `ortho_cortisol_day`, `microbiome` contre `mikrobiom`) : huit
    // analyses prescrites sur neuf repartaient avec zéro marqueur attendu,
    // sans que rien ne le signale. Un contexte clinique appauvri en silence
    // ne se voit pas ; une ligne en console, si.
    if (testCode) {
      console.warn(
        '[catalogue Ortho] aucun marqueur attendu pour le test prescrit :', testCode,
        '— le code existe-t-il encore dans orthoAnalyticTests.js ?',
      );
    }
    return [];
  }
  return test.expected_markers
    .map((markerCode) => MARKERS_CATALOG[markerCode])
    .filter(Boolean);
}

/**
 * Liste tous les codes de tests qui n'ont pas encore de prix renseigné.
 * Utile pour QA / dashboard admin.
 */
export function getTestsWithMissingPrices() {
  return Object.values(ORTHO_TESTS_CATALOG)
    .filter((t) => t.is_active !== false && (t.price_chf === null || t.price_chf === undefined));
}

/**
 * Liste les TODO documentés (prix manquants + scope incomplet).
 */
export function getCatalogTodos() {
  return Object.values(ORTHO_TESTS_CATALOG)
    .filter((t) => t.todo)
    .map((t) => ({ code: t.code, name: t.name, todo: t.todo }));
}
